/**
 * Where the real sky was, on a real night, over a real place on the Western
 * Front — the arithmetic half of ENHANCED SKY's night treatment.
 *
 * Nothing here is the original's. `RB.EXE`'s `sun.c` has one clock hand: an
 * hour angle of `0x0AAB` per hour about a fixed azimuth, with the moon pinned
 * exactly half a turn opposite the sun and the stars carrying a
 * `(day + month * 30) * 0xB6` seasonal shove (`time-of-day.ts`'s header).
 * That is a 1990 sky, and `time-of-day.ts` still draws it: it is what the
 * `original` treatment shows, and the `measured` reference under it. What this
 * module adds, behind ENHANCED SKY and nothing else, is the sky that was actually
 * overhead — so a December 1915 midnight over Verdun puts Orion in the south
 * and the Plough in the north-east because that is where they were.
 *
 * ## Which algorithms, and how wrong they are
 *
 * Jean Meeus, *Astronomical Algorithms* (2nd ed.), in its own low-precision
 * forms. Chapter and equation numbers are quoted at each function. The error
 * budget, against full VSOP87/ELP2000:
 *
 * | quantity | method | error |
 * |---|---|---|
 * | sidereal time | ch. 12, eq. 12.4 (mean, no nutation) | < 1 arcsec |
 * | star positions | ch. 21, eq. 21.3 rigorous precession from J2000 | < 1 arcmin |
 * | the sun | ch. 25 low accuracy | ~ 1 arcmin |
 * | the moon | ch. 47, 27 of 60 longitude terms, 30 of 60 latitude | ~ 3 arcmin |
 * | moon phase angle | ch. 48, eq. 48.3 | ~ 0.1 degree |
 *
 * Every one of those is far inside the "a few degrees is fine" the sky is
 * drawn to, and far inside the 4.1 degrees the port's own moon *disc* subtends
 * (`sky-dressing.ts`). Deliberately **not** modelled: nutation, aberration,
 * proper motion (84 years of it moves Barnard's Star, and no star in the
 * catalogue this feeds is Barnard's Star), refraction, and the observer's
 * parallax — the last of which matters only for the moon, at up to a degree,
 * and only for someone comparing a screenshot against an ephemeris.
 *
 * No three.js and no `MissionSpec` here, the same split
 * `enhanced-sky-look.ts` keeps: everything below is a number in and a number
 * out, which is what `sky-astronomy.test.ts` pins against published almanac
 * values.
 */

import type { Vec3 } from "@red-opus/engine";

/** Where on Earth the observer is. East longitude positive, as every formula below wants it. */
export interface Observer {
  readonly latitudeDeg: number;
  readonly longitudeDeg: number;
}

/** A position on the celestial sphere, in the equatorial frame of some epoch. */
export interface Equatorial {
  readonly rightAscensionDeg: number;
  readonly declinationDeg: number;
}

/** A position in the observer's own sky. Azimuth is compass bearing: 0 north, 90 east. */
export interface Horizontal {
  readonly altitudeDeg: number;
  readonly azimuthDeg: number;
}

const DEG = Math.PI / 180;

/** Degrees folded into `[0, 360)`, which every mean-element polynomial below needs. */
export function wrapDegrees(degrees: number): number {
  return ((degrees % 360) + 360) % 360;
}

function sinDeg(degrees: number): number {
  return Math.sin(degrees * DEG);
}

function cosDeg(degrees: number): number {
  return Math.cos(degrees * DEG);
}

/**
 * The Julian Day of a Gregorian calendar date, `day` carrying the fraction of
 * a day since midnight UT. Meeus ch. 7, eq. 7.1, Gregorian branch only — this
 * port's calendar starts in December 1915 and never goes near 1582.
 */
export function julianDay(year: number, month: number, day: number): number {
  const y = month <= 2 ? year - 1 : year;
  const m = month <= 2 ? month + 12 : month;
  const a = Math.floor(y / 100);
  const b = 2 - a + Math.floor(a / 4);
  return Math.floor(365.25 * (y + 4716)) + Math.floor(30.6001 * (m + 1)) + day + b - 1524.5;
}

/** Julian centuries since J2000.0 — the `T` every polynomial in this file is written in. */
export function julianCenturies(jd: number): number {
  return (jd - 2451545) / 36525;
}

/**
 * Greenwich mean sidereal time in degrees. Meeus eq. 12.4, which is the form
 * that takes the Julian Day directly rather than splitting it at 0h UT.
 */
export function greenwichSiderealDegrees(jd: number): number {
  const t = julianCenturies(jd);
  const theta =
    280.46061837 + 360.98564736629 * (jd - 2451545) + 0.000387933 * t * t - (t * t * t) / 38710000;
  return wrapDegrees(theta);
}

/** The mean obliquity of the ecliptic. Meeus eq. 22.2, truncated at `T³`. */
export function obliquityDegrees(jd: number): number {
  const t = julianCenturies(jd);
  return 23.439291 - 0.0130042 * t - 0.00000016 * t * t + 0.000000504 * t * t * t;
}

/** Ecliptic longitude and latitude onto the equator of the same date. Meeus eq. 13.3/13.4. */
export function equatorialFromEcliptic(longitudeDeg: number, latitudeDeg: number, jd: number): Equatorial {
  const e = obliquityDegrees(jd);
  const sinLat = sinDeg(latitudeDeg);
  const cosLat = cosDeg(latitudeDeg);
  const sinLon = sinDeg(longitudeDeg);
  const ra = Math.atan2(sinLon * cosDeg(e) - (sinLat / cosLat) * sinDeg(e), cosDeg(longitudeDeg));
  const dec = Math.asin(sinLat * cosDeg(e) + cosLat * sinDeg(e) * sinLon);
  return { rightAscensionDeg: wrapDegrees(ra / DEG), declinationDeg: dec / DEG };
}

/**
 * A J2000 catalogue position precessed to the equator and equinox of `jd`.
 *
 * Meeus eq. 21.3, the **rigorous** rotation rather than eq. 21.1's annual
 * drift rates. That choice is not fussiness: the drift form carries a
 * `tan(declination)` and Polaris sits at +89.26 degrees, where the tangent is
 * 84 and a 1916 epoch is 84 years of extrapolation. The rotation has no such
 * pole, and the whole cost is three sines.
 */
export function precessFromJ2000(position: Equatorial, jd: number): Equatorial {
  const t = julianCenturies(jd);
  const arcsec = 1 / 3600;
  const zeta = (2306.2181 * t + 0.30188 * t * t + 0.017998 * t * t * t) * arcsec;
  const z = (2306.2181 * t + 1.09468 * t * t + 0.018203 * t * t * t) * arcsec;
  const theta = (2004.3109 * t - 0.42665 * t * t - 0.041833 * t * t * t) * arcsec;

  const ra0 = position.rightAscensionDeg + zeta;
  const cosDec0 = cosDeg(position.declinationDeg);
  const sinDec0 = sinDeg(position.declinationDeg);
  const a = cosDec0 * sinDeg(ra0);
  const b = cosDeg(theta) * cosDec0 * cosDeg(ra0) - sinDeg(theta) * sinDec0;
  const c = sinDeg(theta) * cosDec0 * cosDeg(ra0) + cosDeg(theta) * sinDec0;
  return {
    rightAscensionDeg: wrapDegrees(z + Math.atan2(a, b) / DEG),
    declinationDeg: Math.asin(Math.min(Math.max(c, -1), 1)) / DEG,
  };
}

/**
 * An equatorial position as the observer sees it. Meeus eq. 13.5/13.6, with
 * the azimuth turned from the book's south-based convention onto the compass
 * bearing the rest of this port speaks (`time-of-day.ts`'s `celestialDirection`
 * takes the same: zero north, increasing east).
 */
export function horizontalOf(position: Equatorial, jd: number, observer: Observer): Horizontal {
  const hourAngle = greenwichSiderealDegrees(jd) + observer.longitudeDeg - position.rightAscensionDeg;
  const sinLat = sinDeg(observer.latitudeDeg);
  const cosLat = cosDeg(observer.latitudeDeg);
  const sinDec = sinDeg(position.declinationDeg);
  const cosDec = cosDeg(position.declinationDeg);
  const altitude = Math.asin(sinLat * sinDec + cosLat * cosDec * cosDeg(hourAngle));
  const southward = Math.atan2(
    sinDeg(hourAngle) * cosDec,
    cosDeg(hourAngle) * cosDec * sinLat - sinDec * cosLat,
  );
  return { altitudeDeg: altitude / DEG, azimuthDeg: wrapDegrees(southward / DEG + 180) };
}

/**
 * A sky position in the port's world axes (`math3d.ts`: `+X` east, `−Z` north,
 * `+Y` up) — the same three lines `celestialDirection` ends on, so the dome,
 * the disc and the star field all agree about which way north is.
 */
export function directionOf(position: Horizontal): Vec3 {
  const horizontal = cosDeg(position.altitudeDeg);
  return {
    x: horizontal * sinDeg(position.azimuthDeg),
    y: sinDeg(position.altitudeDeg),
    z: -horizontal * cosDeg(position.azimuthDeg),
  };
}

/** Where a body is, how far away it is, and where it sits on the ecliptic. */
export interface BodyPosition extends Equatorial {
  readonly eclipticLongitudeDeg: number;
  readonly eclipticLatitudeDeg: number;
  readonly distanceKm: number;
}

/** One astronomical unit, so the sun's `R` and the moon's `Δ` are in the same units for eq. 48.3. */
const ASTRONOMICAL_UNIT_KM = 149597870.7;

/**
 * The sun. Meeus ch. 25's low-accuracy solar coordinates: mean longitude, mean
 * anomaly, the equation of the centre to three terms, and the radius vector
 * from the true anomaly. Good to about an arcminute, which is a tenth of the
 * sun's own diameter.
 *
 * At night the altitude this ends up at is negative and nothing draws it — but
 * it is still needed, because it is what says which way the moon's lit side
 * faces.
 */
export function sunPosition(jd: number): BodyPosition {
  const t = julianCenturies(jd);
  const meanLongitude = wrapDegrees(280.46646 + 36000.76983 * t + 0.0003032 * t * t);
  const meanAnomaly = wrapDegrees(357.52911 + 35999.05029 * t - 0.0001537 * t * t);
  const eccentricity = 0.016708634 - 0.000042037 * t - 0.0000001267 * t * t;
  const centre =
    (1.914602 - 0.004817 * t - 0.000014 * t * t) * sinDeg(meanAnomaly) +
    (0.019993 - 0.000101 * t) * sinDeg(2 * meanAnomaly) +
    0.000289 * sinDeg(3 * meanAnomaly);
  const trueLongitude = wrapDegrees(meanLongitude + centre);
  const trueAnomaly = meanAnomaly + centre;
  const radiusAu =
    (1.000001018 * (1 - eccentricity * eccentricity)) / (1 + eccentricity * cosDeg(trueAnomaly));
  return {
    ...equatorialFromEcliptic(trueLongitude, 0, jd),
    eclipticLongitudeDeg: trueLongitude,
    eclipticLatitudeDeg: 0,
    distanceKm: radiusAu * ASTRONOMICAL_UNIT_KM,
  };
}

/**
 * Meeus table 47.A, the 27 largest terms of 60. Each row is
 * `D, M, M', F, Σl (1e-6 deg), Σr (1e-3 km)`.
 *
 * The head of this table is the moon's whole character: row 0 is the equation
 * of the centre (6.29 degrees), row 1 the evection Ptolemy found (1.27), row 2
 * the variation Tycho found (0.66), row 4 the yearly equation (0.19). The 33
 * dropped rows are all below 0.004 degrees and sum, in the worst case, to
 * under two arcminutes.
 */
const MOON_LONGITUDE_TERMS: readonly number[] = [
  0, 0, 1, 0, 6288774, -20905355,
  2, 0, -1, 0, 1274027, -3699111,
  2, 0, 0, 0, 658314, -2955968,
  0, 0, 2, 0, 213618, -569925,
  0, 1, 0, 0, -185116, 48888,
  0, 0, 0, 2, -114332, -3149,
  2, 0, -2, 0, 58793, 246158,
  2, -1, -1, 0, 57066, -152138,
  2, 0, 1, 0, 53322, -170733,
  2, -1, 0, 0, 45758, -204586,
  0, 1, -1, 0, -40923, -129620,
  1, 0, 0, 0, -34720, 108743,
  0, 1, 1, 0, -30383, 104755,
  2, 0, 0, -2, 15327, 10321,
  0, 0, 1, 2, -12528, 0,
  0, 0, 1, -2, 10980, 79661,
  4, 0, -1, 0, 10675, -34782,
  0, 0, 3, 0, 10034, -23210,
  4, 0, -2, 0, 8548, -21636,
  2, 1, -1, 0, -7888, 24208,
  2, 1, 0, 0, -6766, 30824,
  1, 0, -1, 0, -5163, -8379,
  1, 1, 0, 0, 4987, -16675,
  2, -1, 1, 0, 4036, -12831,
  2, 0, 2, 0, 3994, -10445,
  4, 0, 0, 0, 3861, -11650,
  2, 0, -3, 0, 3665, 14403,
];

/** Meeus table 47.B, the 30 largest terms of 60: `D, M, M', F, Σb (1e-6 deg)`. */
const MOON_LATITUDE_TERMS: readonly number[] = [
  0, 0, 0, 1, 5128122,
  0, 0, 1, 1, 280602,
  0, 0, 1, -1, 277693,
  2, 0, 0, -1, 173237,
  2, 0, -1, 1, 55413,
  2, 0, -1, -1, 46271,
  2, 0, 0, 1, 32573,
  0, 0, 2, 1, 17198,
  2, 0, 1, -1, 9266,
  0, 0, 2, -1, 8822,
  2, -1, 0, -1, 8216,
  2, 0, -2, -1, 4324,
  2, 0, 1, 1, 4200,
  2, 1, 0, -1, -3359,
  2, -1, -1, 1, 2463,
  2, -1, 0, 1, 2211,
  2, -1, -1, -1, 2065,
  0, 1, -1, -1, -1870,
  4, 0, -1, -1, 1828,
  0, 1, 0, 1, -1794,
  0, 0, 0, 3, -1749,
  0, 1, -1, 1, -1565,
  1, 0, 0, 1, -1491,
  0, 1, 1, 1, -1475,
  0, 1, 1, -1, -1410,
  0, 1, 0, -1, -1344,
  1, 0, 0, -1, -1335,
  0, 0, 3, 1, 1107,
  4, 0, 0, -1, 1021,
  4, 0, -1, 1, 833,
];

/** The mean distance the `Σr` sum is a correction to, Meeus ch. 47. */
const MOON_MEAN_DISTANCE_KM = 385000.56;

/**
 * The moon. Meeus ch. 47's five fundamental arguments driven through the two
 * truncated tables above, with the `E` factor applied to every term that
 * carries the sun's mean anomaly — that is the eccentricity of *Earth's* orbit
 * slowly changing, and leaving it out is the classic way to be a few
 * arcminutes wrong in exactly the terms that matter at new and full moon.
 */
export function moonPosition(jd: number): BodyPosition {
  const t = julianCenturies(jd);
  const meanLongitude = wrapDegrees(
    218.3164477 + 481267.88123421 * t - 0.0015786 * t * t + (t * t * t) / 538841 - (t * t * t * t) / 65194000,
  );
  const elongation = wrapDegrees(
    297.8501921 + 445267.1114034 * t - 0.0018819 * t * t + (t * t * t) / 545868 - (t * t * t * t) / 113065000,
  );
  const sunAnomaly = wrapDegrees(357.5291092 + 35999.0502909 * t - 0.0001536 * t * t + (t * t * t) / 24490000);
  const moonAnomaly = wrapDegrees(
    134.9633964 + 477198.8675055 * t + 0.0087414 * t * t + (t * t * t) / 69699 - (t * t * t * t) / 14712000,
  );
  const latitudeArgument = wrapDegrees(
    93.272095 + 483202.0175233 * t - 0.0036539 * t * t - (t * t * t) / 3526000 + (t * t * t * t) / 863310000,
  );
  const e = 1 - 0.002516 * t - 0.0000074 * t * t;

  let sumL = 0;
  let sumR = 0;
  for (let at = 0; at < MOON_LONGITUDE_TERMS.length; at += 6) {
    const angle = argumentOf(MOON_LONGITUDE_TERMS, at, elongation, sunAnomaly, moonAnomaly, latitudeArgument);
    const damping = e ** Math.abs(MOON_LONGITUDE_TERMS[at + 1] ?? 0);
    sumL += (MOON_LONGITUDE_TERMS[at + 4] ?? 0) * sinDeg(angle) * damping;
    sumR += (MOON_LONGITUDE_TERMS[at + 5] ?? 0) * cosDeg(angle) * damping;
  }
  let sumB = 0;
  for (let at = 0; at < MOON_LATITUDE_TERMS.length; at += 5) {
    const angle = argumentOf(MOON_LATITUDE_TERMS, at, elongation, sunAnomaly, moonAnomaly, latitudeArgument);
    sumB += (MOON_LATITUDE_TERMS[at + 4] ?? 0) * sinDeg(angle) * e ** Math.abs(MOON_LATITUDE_TERMS[at + 1] ?? 0);
  }

  const longitude = wrapDegrees(meanLongitude + sumL / 1e6);
  const latitude = sumB / 1e6;
  return {
    ...equatorialFromEcliptic(longitude, latitude, jd),
    eclipticLongitudeDeg: longitude,
    eclipticLatitudeDeg: latitude,
    distanceKm: MOON_MEAN_DISTANCE_KM + sumR / 1000,
  };
}

/** One row's `D·d + M·m + M'·m' + F·f`, the angle its sine or cosine is taken of. */
function argumentOf(
  table: readonly number[],
  at: number,
  elongation: number,
  sunAnomaly: number,
  moonAnomaly: number,
  latitudeArgument: number,
): number {
  return (
    (table[at] ?? 0) * elongation +
    (table[at + 1] ?? 0) * sunAnomaly +
    (table[at + 2] ?? 0) * moonAnomaly +
    (table[at + 3] ?? 0) * latitudeArgument
  );
}

/** How much of the moon's disc is lit, and which way it is heading. */
export interface MoonPhase {
  /** Sun–moon–Earth angle: 0 at full, 180 at new. What the terminator's ellipse is drawn from. */
  readonly phaseAngleDeg: number;
  /** `(1 + cos(phase angle)) / 2` — the fraction of the visible disc that is lit. */
  readonly illuminatedFraction: number;
  /** True from new through full: the lit limb is the western one, and the moon is getting fuller. */
  readonly waxing: boolean;
}

/**
 * Meeus ch. 48. The elongation `ψ` is the angle between the two bodies as seen
 * from Earth (eq. 48.2); the phase angle `i` is the angle at the *moon*
 * between the sun and the Earth, which eq. 48.3 recovers from the elongation
 * and the two distances.
 *
 * The distances are why this is not simply `180 − ψ`: the sun is 390 times
 * further away than the moon, so the correction is at most about a quarter of
 * a degree — small, but it is the whole difference between a phase that is
 * right and one that is arbitrarily close to right, and it costs an `atan2`.
 */
export function moonPhaseOf(sun: BodyPosition, moon: BodyPosition): MoonPhase {
  const cosElongation =
    sinDeg(sun.declinationDeg) * sinDeg(moon.declinationDeg) +
    cosDeg(sun.declinationDeg) *
      cosDeg(moon.declinationDeg) *
      cosDeg(sun.rightAscensionDeg - moon.rightAscensionDeg);
  const elongation = Math.acos(Math.min(Math.max(cosElongation, -1), 1));
  const phase = Math.atan2(
    sun.distanceKm * Math.sin(elongation),
    moon.distanceKm - sun.distanceKm * Math.cos(elongation),
  );
  return {
    phaseAngleDeg: phase / DEG,
    illuminatedFraction: (1 + Math.cos(phase)) / 2,
    waxing: wrapDegrees(moon.eclipticLongitudeDeg - sun.eclipticLongitudeDeg) < 180,
  };
}
